import {Component, HostListener, OnInit} from '@angular/core';
import {MessageService, SelectItem} from "primeng/api";
import {CommonMessagesService} from "../../../services/common-messages.service";
import {AuthGuard} from "../../../common/auth-guard.service";
import {TranslocoService} from "@ngneat/transloco";
import {TimezoneService} from "../../../services/timezone.service";
import {FiltersService} from "../../../services/filters.service";
import {SortOrder} from "../../../model/sort-order";
import * as moment from "moment";
import {DefaultDeviceCatalogFilters} from "../../../model/filters/default-device-catalog-filters";
import {DefaultDeviceCatalog} from "../../../model/default-device-catalog";
import {HwCatalogService} from "../../../services/hw-catalog.service";
import {DefaultDeviceCategoriesService} from "../../../services/default-device-categories.service";
import {DefaultAssetRepresentationsService} from "../../../services/default-asset-representations.service";

@Component({
  selector: 'app-default-devices',
  templateUrl: './default-devices.component.html',
  styleUrls: ['./default-devices.component.scss']
})
export class DefaultDevicesComponent implements OnInit {

  public defaultDevicesCatalog: DefaultDeviceCatalog[];
  public totalCatalog: number;
  public loadingCatalog: boolean;

  public rows = 10;
  public first = 0;
  public selected = [];

  public filtersVisible: boolean;
  public currentFilters: DefaultDeviceCatalogFilters;
  public filtersFilled: boolean;
  public creationRangeDates: Date[];
  public updateRangeDates: Date[];

  public defaultDeviceCategories: SelectItem[];
  public defaultAssetRepresentations: SelectItem[];
  public types: SelectItem[];

  public activeButtonState: boolean;

  constructor(private hwCatalogService: HwCatalogService,
              private defaultDeviceCategoriesService: DefaultDeviceCategoriesService,
              private defaultAssetRepresentationsService: DefaultAssetRepresentationsService,
              private commonMessagesService: CommonMessagesService,
              private messageService: MessageService,
              public authGuard: AuthGuard,
              private i18n: TranslocoService,
              private timezoneService: TimezoneService,
              private filtersService: FiltersService<DefaultDeviceCatalogFilters>) {
    this.activeButtonState = true;
    this.currentFilters = new DefaultDeviceCatalogFilters();
  }

  ngOnInit(): void {
    if (this.filtersService.getCurrentFilters(this.constructor.name) !== undefined) {
      this.filtersFilled = true;
    }
    this.loadFilters().then(() => {
      Object.assign(this.currentFilters, this.filtersService.getCurrentFilters(this.constructor.name));
      if (this.currentFilters?.hasOwnProperty('creationRangeDates')) {
        this.creationRangeDates = [new Date(this.currentFilters.creationRangeDates[0]), new Date(this.currentFilters.creationRangeDates[1])];
      }
      if (this.currentFilters?.hasOwnProperty('updateRangeDates')) {
        this.updateRangeDates = [new Date(this.currentFilters.updateRangeDates[0]), new Date(this.currentFilters.updateRangeDates[1])];
      }
    });
    this.defaultDevicesCatalog = new Array(this.rows);
  }

  private loadDefaultDevicesCatalog(offset: number, limit: number): void {
    this.loadingCatalog = true;
    this.defaultDevicesCatalog = new Array(this.rows);
    this.trimFieldsForm(this.currentFilters);
    this.formatterDateForm(this.creationRangeDates, 'creationRangeDates');
    this.formatterDateForm(this.updateRangeDates, 'updateRangeDates');
    this.hwCatalogService.getDefaultDevicesCatalog(offset, limit, this.currentFilters)
      .subscribe(response => {
        this.selected = [];
        this.activeButtonState = true;
        this.defaultDevicesCatalog = response.defaultDevicesCatalog;
        this.totalCatalog = response.totalElements;
        this.loadingCatalog = false;
      });
  }

  private loadFilters(): Promise<any> {
    let promises: Array<Promise<any>> = [
      this.defaultDeviceCategoriesService.getDefaultDeviceCategories().toPromise(),
      this.defaultAssetRepresentationsService.getDefaultAssetRepresentations().toPromise(),
    ];
    return Promise.all(promises).then(response => {
      this.defaultDeviceCategories = response[0].defaultDeviceCategories
        .map(category => category.name)
        .filter((v, i, a) => a.indexOf(v) === i)
        .sort()
        .map(categoryName => {
          return {
            label: categoryName,
            value: categoryName
          };
        });
      this.defaultAssetRepresentations = response[1].defaultAssetRepresentations
        .map(representation => representation.name)
        .filter((v, i, a) => a.indexOf(v) === i)
        .sort()
        .map(representationName => {
          return {
            label: representationName,
            value: representationName
          };
        });
    });
  }

  onPageChange(event): void {
    this.currentFilters.sortField = event.sortField;
    this.currentFilters.sortOrder = event.sortOrder < 0 ? SortOrder.DESCENDING : SortOrder.ASCENDING;
    this.loadDefaultDevicesCatalog(event.first / event.rows, event.rows);
  }

  resetFilters(): void {
    this.currentFilters = new DefaultDeviceCatalogFilters();
    this.currentFilters.sortField = 'name';
    this.currentFilters.sortOrder = SortOrder.ASCENDING;
    this.creationRangeDates = null;
    this.updateRangeDates = null;
    this.filtersFilled = false;
    this.filtersService.setCurrentFilters(undefined, this.constructor.name);
    this.applyFilters(true);
  }

  applyFilters(reset?: boolean): void {
    this.first = 0;
    if (reset !== true) {
      this.filtersService.setCurrentFilters(this.currentFilters, this.constructor.name);
      this.filtersFilled = true;
    }
    this.loadDefaultDevicesCatalog(this.first / this.rows, this.rows);
  }

  confirmAndDelete(defaultDeviceCatalog: DefaultDeviceCatalog): void {
    this.commonMessagesService.showDeleteRequestConfirmation(() => {
      this.deleteDefaultDeviceCatalog(defaultDeviceCatalog.uuid);
    });
  }

  deleteDefaultDeviceCatalog(uuid: string): void {
    this.hwCatalogService.deleteDefaultDeviceCatalog(uuid).toPromise().then(() => {
      this.commonMessagesService.showSuccessfullyDeletedMessage();
      this.loadDefaultDevicesCatalog(this.first / this.rows, this.rows);
    }, error => {
      this.commonMessagesService.showErrorWhileDeletingMessage(error.status);
    });
  }

  confirmAndDeleteSelected(): void {
    this.commonMessagesService.showDeleteRequestConfirmation(() => this.deleteSelected(), true);
  }

  deleteSelected(): void {
    if (this.selected.length === 0) {
      return;
    }
    const uuids = this.selected.map(item => item.uuid);
    this.hwCatalogService.deleteDefaultDevicesCatalog(uuids).toPromise().then(() => {
      this.commonMessagesService.showSuccessfullyDeletedMessage();
      this.loadDefaultDevicesCatalog(this.first / this.rows, this.rows);
    }, error => {
      this.commonMessagesService.showErrorWhileDeletingMessage(error.status, true);
    });
  }

  evaluateActiveButtonState(): void {
    let state;
    this.selected.forEach(event => {
      if (event.active === true) {
        state = true;
      } else if (event.active === false) {
        if (state === undefined) {
          state = false;
        }
      }
    });
    this.activeButtonState = !state;
  }

  changeActiveStates(): void {
    const uuids = this.selected.map(item => item.uuid);
    this.hwCatalogService.updateDefaultDevicesState(uuids, !this.activeButtonState).toPromise().then(() => {
      const summary = this.i18n.translate('success.generic.changesApplied');
      const detail = this.i18n.translate('success.defaultDevices.changedState');
      this.messageService.add({severity: 'success', summary, detail});
      this.loadDefaultDevicesCatalog(this.first / this.rows, this.rows);
    });
  }

  trimFieldsForm(filters: DefaultDeviceCatalogFilters) {
    Object.keys(filters).forEach((key) => {
      if (filters[key] != undefined && typeof filters[key] === 'string') {
        filters[key] = filters[key].trim();
      }
    });
  }

  formatterDateForm(rangeDates: Date[], filtersField: string): void {
    if (rangeDates && rangeDates.length > 1) {
      if (!rangeDates[1]) {
        rangeDates[1] = moment.utc().toDate();
      }
      const utcRange = this.timezoneService.timestampRangeFromCalendarData(rangeDates);
      if (filtersField === 'creationRangeDates') {
        this.currentFilters.creationRangeDates = [utcRange.from, utcRange.until];
      } else {
        this.currentFilters.updateRangeDates = [utcRange.from, utcRange.until];
      }
    }
  }

  @HostListener('window:keyup', ['$event'])
  keyEvent(event: KeyboardEvent): void {
    if (event.key === 'f') {
      this.filtersVisible = true;
    }
    if (event.key === 'Escape') {
      this.filtersVisible = false;
    }
  }

}
